import { get, trim, isUndefined, includes , findIndex } from 'lodash';
import { isEmpty, isLength } from 'validator';
import base64 from 'base-64';
import utf8 from 'utf8';

import {
  ROLE_PORTAL_ADMIN,
  ROLE_API_OWNER,
  ROLE_ORG_ADMIN,
  ROLE_DEVELOPER,
  ROLE_ORG_PUBLISHER,
  PORTAL_STATUS_NEW,
  API_OWNER_TEXT,
  ORGANIZATION_ADMIN_TEXT,
  DEVELOPER_TEXT,
  APPLICATION_DISABLED_BY_TYPE_EXTERNAL,
  APPLICATION_STATUS_ENABLED,
  APPLICATION_STATUS_PENDING_APPROVAL,
  APPLICATION_STATUS_EDIT_PENDING_APPROVAL,
} from '../constants';

const MAX_LENGTH = 255;

const getLastExtension = (fileName, search) => {
  if (!fileName) return null;
  return fileName.substr(fileName.lastIndexOf(search) + 1).toLowerCase();
};

export const getErrorMessage = error => (
  get(error, 'message') && [get(error, 'message')]
);

export const getValidationErrors = (error) => (
  get(error, 'response.data.error.detail.validationErrors') || [error]
);

export const getUserRole = ({
  portalAdmin,
  apiOwner,
  orgAdmin,
  developer,
  orgPublisher,
} = {}) => {
  let roleName;
  if (portalAdmin) {
    roleName = ROLE_PORTAL_ADMIN;
  }
  if (apiOwner) {
    roleName = ROLE_API_OWNER;
  }
  if (orgAdmin) {
    roleName = ROLE_ORG_ADMIN;
  }
  if (developer) {
    roleName = ROLE_DEVELOPER;
  }
  if (orgPublisher) {
    roleName = ROLE_ORG_PUBLISHER;
  }
  return roleName;
};

export const getSingularRoleName = (role) => {
  const ROLE_ROLENAME = {
    apiowners: API_OWNER_TEXT,
    devorgadministrators: ORGANIZATION_ADMIN_TEXT,
    developers: DEVELOPER_TEXT,
  };
  const roleName = ROLE_ROLENAME[role.roleName] || role.roleName;
  return role.organization ? `${role.organization} (${roleName})` : roleName;
};

export const hasPublisherRole = (user) => (
  getUserRole(user) === ROLE_PORTAL_ADMIN || getUserRole(user) === ROLE_API_OWNER
);

export const hasOrgBoundRole = (user) => (
  [ROLE_ORG_ADMIN, ROLE_ORG_PUBLISHER, ROLE_DEVELOPER].includes(getUserRole(user))
);

export const hasDeveloperRole = (user) => (
  getUserRole(user) === ROLE_ORG_ADMIN || getUserRole(user) === ROLE_DEVELOPER
);

export const hasAdminRole = (user) => getUserRole(user) === ROLE_PORTAL_ADMIN;

export const hasOrgPublisherRole = (user) => getUserRole(user) === ROLE_ORG_PUBLISHER;

export const hasAccessPermissionsPage = (portalStatus) => portalStatus === PORTAL_STATUS_NEW;

export const getApiOwnerUser = (userDetails) => {
  const apiOwnerName = `${userDetails.firstName} ${userDetails.lastName}`;
  const apiOwner = { uuid: userDetails.uuid, name: apiOwnerName, roleOrgs: [{ roleName: 'apiOwner' }] };
  return apiOwner;
};

export const hasError = (value, isRequired, max = MAX_LENGTH) =>
  (isRequired && isEmpty(trim(value))) || !isLength(value, { max });

export const isNumericVersion = (value) => {
  const numeric = /^\d+([.|_|-]*\d+)*$/;
  return !numeric.test(value);
};

export const getFileExtension = (filename) => getLastExtension(filename, '.');

export const listToObject = (entities = [], type) => entities.reduce(
  (acc, props) => ({ ...acc, [props.uuid]: { ...props, type } }),
  {},
);

export const isEditApplicationDisabled = (userContext, { status, disabledByType }) => {
  if (hasPublisherRole(userContext)) {   // Internal applications
    return [APPLICATION_STATUS_EDIT_PENDING_APPROVAL].includes(status);
  } else if (hasOrgBoundRole(userContext)) { // External applications
    return !isUndefined(status) && !isUndefined(disabledByType) &&
    (
      !(disabledByType === null || disabledByType === APPLICATION_DISABLED_BY_TYPE_EXTERNAL) ||
      [APPLICATION_STATUS_PENDING_APPROVAL,
        APPLICATION_STATUS_EDIT_PENDING_APPROVAL].includes(status)
    );
  }
  return false;
};

export const isEditApplicationKeysDisabled = (userContext, { status }) => {
  if (hasPublisherRole(userContext)) {   // Internal applications
    return !([APPLICATION_STATUS_ENABLED,
      APPLICATION_STATUS_EDIT_PENDING_APPROVAL].includes(status));
  } else if (hasOrgBoundRole(userContext)) { // External applications
    return true;
  }
  return false;
};

export const readCookie = (name) => {
  const cookieName = `${name}=`;
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') cookie = cookie.substring(1, cookie.length);
    if (cookie.indexOf(cookieName) === 0) return cookie.substring(cookieName.length, cookie.length);
  }
  return null;
};

export const createCookie = (name, value, days, path) => {
  let expires;
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=  ${date.toGMTString()}`;
  } else expires = '';
  document.cookie = `${name}=${value}${expires}; path=${path}`;
};

export const getBundlesExtension = (filename, ALLOWED_FILE_EXTENSIONS) => {
  const last2Index = filename.lastIndexOf('.', filename.lastIndexOf('.') - 1) + 1;
  const extension = filename.substr(last2Index).toLocaleLowerCase();
  if (includes(ALLOWED_FILE_EXTENSIONS, extension)) {
    return extension;
  }
  return getLastExtension(filename, '.');
};

const entityMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

const escapeHtml = (str) => str.replace(/[&<>"'`=/]/g, (s) => entityMap[s]);

const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const formatStatusMessage = (message) => {
  let modalBody = '';
  let jsonObj = null;

  if (message) {
    if (isJsonString(message)) {
      jsonObj = JSON.parse(message);
    }
    if (jsonObj && jsonObj.length) {
      for (let i = 0; i < jsonObj.length; i++) {
        modalBody += jsonObj[i].message;
      }
    } else if (jsonObj) {
      modalBody = JSON.stringify(jsonObj, null, 2);
    } else {
      modalBody = escapeHtml(message);
    }
  }
  return modalBody;
};

export const getVersion = (version, char = '.') => {
  const len = version ? version.indexOf(char, version.indexOf(char) + 1) : 0;
  return len > 0 ? version.substr(0, len) : version;
};

export const getTitleCaseText = (value) => {
  const str = value ? value.replace('_', ' ') : value;
  return str ? str.toLowerCase().replace(/\b(\w)/g, s => s.toUpperCase()) : str;
};

export const getTabIndex = (tabs, match) => {
  const tabName = get(match, 'params.tabName');
  const index = findIndex(tabs, tab => (tab.id === tabName));
  // if none found default to 0
  return (index > 0 ? index : 0);
};

export const getCookieMessage = (message) => {
  const text = utf8.decode(base64.decode(message));
  return text
    .replace(/<script>.*<\/script>/g, '')
    .replace(/javascript:([^"]+)/g, '')
    .replace(/(o|&#111;)(n|&#110;)[\S]*=([^)]+)/g, '');
};

/* converHexToRGB */
export const convertHexToRGB = (hex = '', a = 1) => {
  const hexValue = hex.replace('#', '');
  const r = parseInt(hexValue.substring(0, 2), 16);
  const g = parseInt(hexValue.substring(2, 4), 16);
  const b = parseInt(hexValue.substring(4, 6), 16);

  return `rgba(${r},${g},${b},${a})`;
};

/* eslint-disable */
/*
found from: https://stackoverflow.com/a/2117523
 */
export const uuidv4 = () => {
  return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
};
/* eslint-enable */



// WEBPACK FOOTER //
// ./src/utils/index.js